/*
  Functions to update the backend map viewer context, using the MapViewerServletNG endpoint.
  Was taken out of the more general maps-api so it can be used without pulling in the whole Open
  Layers dependency.
 */

import { ajax, createEventSource } from 'cadenza/api-client/api';
import { MIMETYPE_JSON } from 'cadenza/api-client/api-constants';
import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';

interface MapContextProperties {
  size?: DimensionDto;
  extent?: WorldBoxDto;
  scale?: number;
  layers?: string[];
  hiddenLayers?: string[];
  selectedLayer?: string;
  normalGroupChoices?: NormalGroupChoices;
}

interface NormalGroupChoices {
  readonly [key: string]: GroupChoiceDto;
}

interface GroupChoiceDto {
  uid: string;
  name: string;
  originalLayerOrderIndex: number;
  visible: boolean;
}

export class MapContextApi {

  readonly #mapId: string;

  constructor ({ mapId }: { mapId: string }) {
    this.#mapId = mapId;
  }

  /**
   * Update all the properties of the map context.
   * See MapSettingsEndpointController for details about the parameters,
   * format to use and which parameters should be set.
   *
   * @param props - An object containing all the properties to update.
   * @return promise for the Ajax request
   */
  updateContext (props: MapContextProperties): Promise<void> {
    const settings = {
      mapSizeWidth: props.size ? props.size[0] : null,
      mapSizeHeight: props.size ? props.size[1] : null,
      boundingBox: props.extent ? props.extent : null,
      scale: props.scale,
      layers: props.layers ? props.layers : null,
      hiddenLayers: props.hiddenLayers ? props.hiddenLayers : null,
      selectedLayer: props.selectedLayer || null,
      normalGroupChoices: props.normalGroupChoices || null
    };

    // do not send null attributes
    removeNullAttributes(settings);

    const data = JSON.stringify(settings);

    return ajax(`/maps/${this.#mapId}`, {
      method: 'PUT',
      data,
      contentType: MIMETYPE_JSON
    });
  }

  /**
   * Update only the extent in the backend map context.
   *
   * @param extent - Extent, as an object with the following properties: minX, minY, maxX, maxY
   * @return promise for the Ajax request
   */
  updateMapExtent (extent: WorldBoxDto): Promise<void> {
    return this.updateContext({ extent });
  }

  getContext (contextUrl: string, [ width, height ]: DimensionDto) {
    return ajax(contextUrl, { data: { width, height } });
  }

  getOverviewContext () {
    return ajax(`/maps/${this.#mapId}/overview`);
  }

  createMapStatusEventSource () {
    return createEventSource(`/maps/${this.#mapId}/status`);
  }

}

function removeNullAttributes (object: Record<string, unknown>) {
  Object.keys(object).forEach(key => {
    if (object[key] === null || object[key] === undefined) {
      delete object[key];
    }
  });
}
